// SPDX-FileCopyrightText: Copyright (c) 2025 Software GmbH, Darmstadt, Germany and/or its subsidiaries and/or its affiliates
// SPDX-FileContributor: Martin Heß
// SPDX-FileContributor: Zead Alshukairi (planen-bauen 4.0 GmbH)
// SPDX-FileContributor: Miguel Vega (planen-bauen 4.0 GmbH)
//
// SPDX-License-Identifier: Apache-2.0

import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from "@angular/common/http";
import { DIDEntry, DIDs } from '../models';
import { environment } from "../../../../environments/environment";
import { Observable } from "rxjs";

@Injectable({
  providedIn: 'root'
})
/*
 * Allows access to store, retrieve and delete DID entries for users and institutions.
 */
export class DidEntryMgmtService {

  private selfHosted = (location.hostname != environment.this.ourDomain);

  constructor(private http: HttpClient) { }

  /**
   * Checks if a key suffix already exists.
   *
   * @param keySuffix the key suffix to check
   * @returns an Observable emitting true if exists, false otherwise
   */
  checkKeySuffixExits(keySuffix: string): Observable<boolean> {
    let params = new HttpParams().set('keySuffix', keySuffix);
    return this.http.get<boolean>(environment.server.endpoints.did_manager.exists, { params: params });
  }

  /**
   * Retrieves user DIDs with pagination.
   *
   * @param ownerId the owner ID
   * @param isInstitution true if the owner is an institution, false otherwise
   * @param page the page number (0-based)
   * @param pageSize the number of items per page
   * @returns an Observable emitting the paginated DIDs
   */
  getUserDIDs(ownerId: number, isInstitution: boolean, page: number, pageSize: number): Observable<DIDs> {
    let params = this.buildHttpParams(
      ['ownerId', 'isInstitutionDID', 'page', 'pageSize'], 
      [ownerId+"", isInstitution+"", page+"", pageSize+""]
    );
    return this.http.get<DIDs>(environment.server.endpoints.did_manager.did, { params: params });
  }

  /**
   * Stores a user DID in the backend.
   *
   * @param did the DID to store
   * @param label optional label
   * @param keySuffix optional key suffix
   * @returns an Observable emitting the stored DID entry
   */
  storeUserDID(ownerId: number, isInstitution: boolean, did: string, label?: string, keySuffix?: string) {
    let params = this.buildHttpParams(
      ['ownerId', 'isInstitutionDID', 'did', 'label', 'keySuffix', 'selfHosted'],
      [ownerId+"", isInstitution+"", did, label, keySuffix, this.selfHosted+""]
    );
    return this.http.post<DIDEntry>(environment.server.endpoints.did_manager.did, null, { params: params });
  }

  /**
   * Deletes a user DID from the backend.
   *
   * @param did the DID to delete
   * @returns an Observable that completes when deletion is done
   */
  deleteUserDID(ownerId: number, did: string) {
    let params = this.buildHttpParams(
      ['ownerId', 'selfHosted', 'did'],
      [ownerId+"", this.selfHosted+"", did]
    );
    return this.http.delete(environment.server.endpoints.did_manager.did, { params: params });
  }

  /**
   * Retrieves all institution DIDs.
   *
   * @returns an Observable emitting the institution DIDs
   */
  getInstitutionDIDs(): Observable<DIDEntry[]> {
    return this.http.get<DIDEntry[]>(environment.server.endpoints.did_manager.institution);
  }

  private buildHttpParams(keys: string[], values: string[]): HttpParams {
    let params = new HttpParams();
    for (let i = 0; i < keys.length; i++) {
      params = params.set(keys[i], values[i]);
    }
    return params;
  }
}
